import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import { toast } from "react-toastify";

import TextView from "../../../components/TextView";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import BrandWheelLoader from "../../../components/BrandWheelLoader";
import MediaRecorder, { canRecordMedia } from "../../../components/MediaCapture";
import WebcamPhotoCapture from "../../../components/MediaCapture/WebcamPhotoCapture";
import { CapturePreviewThumb } from "./CapturePreview";
import { ComposerLineArtIcon } from "../../../assets/icons/ComposerLineArtIcon";
import { colors } from "../../../utils";
import { uploadFile } from "../../../utils/s3Helper";
import { audioAcceptAttribute } from "../../../utils/audioFormats";
import { getLoggedInUser } from "../../../utils/helpers";
import { useCreateCaptureItem, useUpdateCaptureItem } from "../../../services/capture";
import { LINK_KIND, linkHost, linkKind, splitPastedLink } from "../captureLinks";
import {
  CAPTURE_TYPES,
  captureTypeLabel,
  detectCaptureType,
  getCaptureUploadPath,
  isWithinSizeLimit,
  maxSizeMbForType,
  validateComposer,
  buildCreateCapturePayload,
  buildUpdateCapturePayload,
  getLastCaptureType,
  setLastCaptureType,
  describeCaptureSaveError,
} from "../Capture.helpers";
import {
  ModalBody,
  TypeTabs,
  TypeTab,
  Dropzone,
  PreviewWrap,
  MediaSlot,
  ModalActions,
  ActionBtnWrap,
  UploadingOverlay,
} from "../styles";

const StyledDialog = styled(Dialog)(() => ({
  ".MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  ".MuiPaper-root": {
    padding: "8px 8px 16px",
    border: "1px solid #E1E1E1",
    borderRadius: 12,
  },
}));

const FieldLabel = styled("label")({
  display: "block",
  width: "100%",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "16px",
  color: colors.textGrey,
  fontFamily: '"DM Sans", sans-serif',
  margin: "0 0 4px",
});

const CharCount = styled("span")(({ atLimit }) => ({
  display: "block",
  width: "100%",
  textAlign: "right",
  fontSize: 11,
  fontFamily: '"DM Sans", sans-serif',
  color: atLimit ? colors.error : colors.textGrey,
  margin: "2px 0 0",
}));

const DOC_ACCEPT =
  ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain";
// Uploaded-file accepts mirror the backend presigned-URL + capture allowlists
// (settings.FILE_TYPE_EXTENSIONS): videos = mp4/avi/mov/mkv; audios = mp3/wav
// PLUS mp4 (the browser recorder captures audio into an mp4/AAC container, and
// the shared allowlist has no .m4a/.aac — so a recorded audio clip legitimately
// arrives as .mp4). Keeping the picker in step with the server allowlist means
// the file chosen is one that will actually upload, not a silent dead-end.
const VIDEO_ACCEPT =
  ".mp4,.mov,.avi,.mkv,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska";
// QA-97 — built from the ONE audio-format list (src/utils/audioFormats.js),
// which mirrors settings.FILE_TYPE_EXTENSIONS["audios"] on the backend. A
// hand-kept second copy of this list is exactly how .m4a went missing on both
// sides for months, so there is no second copy any more.
const AUDIO_ACCEPT = audioAcceptAttribute();

// Composer type tabs (order mirrors mobile: note, photo, video, audio, doc).
const COMPOSER_TABS = [
  { type: CAPTURE_TYPES.TEXT, label: "Notes", lineArt: "pencil" },
  { type: CAPTURE_TYPES.PHOTO, label: "Photo", Icon: ImageOutlinedIcon },
  { type: CAPTURE_TYPES.VIDEO, label: "Video", Icon: VideocamOutlinedIcon },
  { type: CAPTURE_TYPES.AUDIO, label: "Audio", lineArt: "mic" },
  { type: CAPTURE_TYPES.DOC, label: "Document", Icon: DescriptionOutlinedIcon },
  { type: CAPTURE_TYPES.LINK, label: "Link", lineArt: "link" },
];

// `name` (Task 2, 2026-08-06) rides alongside the media fields it describes:
// clearing/replacing a staged file clears its name too, so the next upload
// starts from a clean default instead of carrying over a stale one.
const BLANK_MEDIA_FIELDS = { mediaKey: "", mediaUrl: "", fileName: "", name: "" };

// QA-98/QA-99 — a fresh draft carries no source URL. It is a draft field of its
// own rather than being smuggled through textContent, because on a LINK it is
// the PAYLOAD and on a media capture it is PROVENANCE, and conflating the two
// with the note is how a note ends up in a URL column.
const BLANK_LINK_FIELDS = { sourceUrl: "" };

// Media-acquisition sub-mode within a media tab.
const SOURCE = { UPLOAD: "upload", CAMERA: "camera", RECORD: "record" };

// ISP-9999 — neither field had any length governance: a 500+ char Title
// silently overflowed its box (scrollWidth 3182 vs 426px visible) and Notes
// swallowed a 5016-char paste with no feedback at all. The backend enforces
// no discoverable limit of its own, so these are sensible product caps, not
// a measured server-side ceiling.
const TITLE_MAX_LENGTH = 120;
const NOTES_MAX_LENGTH = 2000;

// Types that carry an optional free-text note/caption in the composer. Every
// media type now qualifies (QA-82/QA-90): the backend accepts text_content on
// VIDEO/AUDIO as of Backend #1863 (merged 2026-08-05), so the "Add a note" step
// is universal and its value is preserved across a tab switch on every type.
const CAPTIONABLE = [
  CAPTURE_TYPES.TEXT,
  CAPTURE_TYPES.PHOTO,
  CAPTURE_TYPES.DOC,
  CAPTURE_TYPES.VIDEO,
  CAPTURE_TYPES.AUDIO,
  // A link's note is its title — worth keeping when the user switches tabs.
  CAPTURE_TYPES.LINK,
];

const acceptFor = (type) => {
  if (type === CAPTURE_TYPES.PHOTO) return "image/*";
  if (type === CAPTURE_TYPES.VIDEO) return VIDEO_ACCEPT;
  if (type === CAPTURE_TYPES.AUDIO) return AUDIO_ACCEPT;
  return DOC_ACCEPT;
};

/**
 * QA-98 — tell the user, before they save, what this link will BECOME in a
 * Segment. The mapping is the same one `captureToBlock` performs on insert, so
 * the hint cannot promise something the drop will not deliver.
 */
const linkKindHint = (url) => {
  const kind = linkKind(url);
  const host = linkHost(url);
  const where = host ? ` from ${host}` : "";
  if (kind === LINK_KIND.VIDEO) return `Video${where} — adds as a Video block.`;
  if (kind === LINK_KIND.IMAGE) return `Image${where} — adds as an Image block.`;
  return `Page${where} — adds as a Text block with the link.`;
};

const recorderModeFor = (type) =>
  type === CAPTURE_TYPES.AUDIO ? "audio" : "video";

/** "a" / "an" for a capture-type word ("a photo" but "an audio"). */
const articleFor = (type) => (/^[aeiou]/i.test(String(type)) ? "an" : "a");

/**
 * Fresh "New capture" draft.
 *
 * `initialType` (QA-101) is the category the user is standing on — the active
 * Capture filter chip — and wins when the caller supplies one, because it is a
 * thing on screen right now. With none supplied the CAP-4 "last type you
 * picked" memory still applies, so that behaviour is preserved rather than
 * quietly reverted.
 */
const emptyDraft = (initialType) => ({
  type: initialType || getLastCaptureType(),
  textContent: "",
  ...BLANK_MEDIA_FIELDS,
  ...BLANK_LINK_FIELDS,
});

/** Seed the composer from an existing item for edit mode (CAP-3). */
const draftFromItem = (item) => ({
  type: item.type,
  textContent: item.text_content || "",
  mediaKey: item.media_key || "",
  mediaUrl: item.media_url || "",
  fileName: "",
  sourceUrl: item.source_url || "",
  // Task 2 (2026-08-06): pre-fill from the item's current name. Falls back to
  // "" when the field is absent -- either a pre-existing item captured before
  // the field existed, or the parallel BE PR simply hasn't deployed yet.
  name: item.title || item.name || "",
});

const AddCaptureModal = ({ open, editItem, initialType, onClose }) => {
  const isEditing = !!editItem;
  const [draft, setDraft] = useState(() => emptyDraft(initialType));
  const [source, setSource] = useState(SOURCE.UPLOAD);
  const [uploading, setUploading] = useState(false);
  const createItem = useCreateCaptureItem();
  const updateItem = useUpdateCaptureItem();
  const userId = getLoggedInUser()?.id;
  const submitting = isEditing ? updateItem.isLoading : createItem.isLoading;
  // The single source of truth for "is this composer submittable?" -- the same
  // validator handleSubmit runs. Computing it here lets the footer button be
  // DISABLED when there is nothing to save, instead of being clickable and
  // failing into a toast (QA-35).
  const composerError = validateComposer(draft);

  // (Re)seed the composer every time it opens -- the modal stays mounted
  // across open/close cycles (Capture/index.jsx renders it unconditionally),
  // so a one-time useState initializer would only ever seed once. Reseeds
  // either from the last-picked type (fresh capture, CAP-4) or from the item
  // being edited (CAP-3), and resets the media source sub-mode to Upload.
  useEffect(() => {
    if (!open) return;
    setDraft(isEditing ? draftFromItem(editItem) : emptyDraft(initialType));
    setSource(SOURCE.UPLOAD);
    setUploading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editItem?.id, initialType]);

  const handleClose = () => {
    if (uploading || submitting) return;
    onClose();
  };

  const setType = (type) => {
    setSource(SOURCE.UPLOAD);
    // Preserve an in-progress note only when moving to a captionable tab
    // (text/photo/doc); never carry it into video/audio, which have no caption.
    const keepCaption = CAPTIONABLE.includes(type);
    setDraft({
      ...BLANK_MEDIA_FIELDS,
      ...BLANK_LINK_FIELDS,
      type,
      textContent: keepCaption ? draft.textContent : "",
      name: draft.name,
    });
    // Remember the last-picked type so a fresh composer reopens on it (CAP-4).
    setLastCaptureType(type);
  };

  /**
   * QA-121 — a link paste is rarely a bare URL ("Title https://…" is how
   * sharing actually hands it over). Split it at paste time so the field ends
   * up holding just the URL and the title lands in the note, instead of
   * failing `captureLinkError`'s scheme check with no clue why. Only steps in
   * when there IS a title to split off (`splitPastedLink` returns null for an
   * already-bare URL) — a normal paste is left to the browser exactly as
   * before. Never overwrites a note the user already typed.
   */
  const handleLinkPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") ?? "";
    const split = splitPastedLink(pasted);
    if (!split) return;
    e.preventDefault();
    setDraft({
      ...draft,
      sourceUrl: split.url,
      textContent: draft.textContent || split.title,
    });
  };

  // Shared: upload a File to the user's capture space and stage it on the draft.
  // The staged media_key is the S3 KEY the presigned upload returns — never a
  // blob:/object URL — so the create payload always references real S3 storage.
  const uploadAndStage = async (file) => {
    // Pass the PLAIN `uploads/<id>/capture/` prefix. Per-upload key uniqueness
    // is now guaranteed by the shared primitive itself
    // (utils/uploadObjectKey.buildUniqueObjectKey, called by uploadFile), so
    // folding a token in here as well would only double it. The prefix is
    // handed over byte-for-byte because the backend's source="capture" stamp is
    // a startswith() on it.
    const { fileKey, location } = await uploadFile(
      file,
      getCaptureUploadPath(userId)
    );
    setDraft((prev) => ({
      ...prev,
      mediaKey: fileKey,
      mediaUrl: location,
      fileName: file.name,
      // Task 2 (2026-08-06): default Name to the uploaded file's name the
      // moment a file is chosen — but never clobber a name the user already
      // typed (e.g. typed ahead of picking a file). Replace clears `name`
      // back to "" first (below), so a genuine replace still re-defaults.
      name: prev.name ? prev.name : file.name,
    }));
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const maxMb = maxSizeMbForType(draft.type);
    if (!isWithinSizeLimit(file, maxMb)) {
      toast.error(`File is too large. Max ${maxMb}MB.`);
      return;
    }

    // Guard an obvious type mismatch for the strict media tabs.
    const detected = detectCaptureType(file);
    const strictTabs = [
      CAPTURE_TYPES.PHOTO,
      CAPTURE_TYPES.VIDEO,
      CAPTURE_TYPES.AUDIO,
    ];
    if (strictTabs.includes(draft.type) && detected !== draft.type) {
      toast.error(
        `Please choose ${articleFor(draft.type)} ${draft.type} file for ` +
          `${articleFor(draft.type)} ${draft.type} capture.`
      );
      return;
    }

    try {
      setUploading(true);
      await uploadAndStage(file);
    } catch (err) {
      console.error("Capture upload failed:", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Webcam still photo → upload → stage → return to the staged preview.
  const handleWebcamPhoto = async (file) => {
    try {
      setUploading(true);
      await uploadAndStage(file);
      setSource(SOURCE.UPLOAD); // show the staged preview
    } catch (err) {
      console.error("Webcam photo upload failed:", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Recorder "Use this recording": upload the recorded clip → stage → preview.
  // Throwing keeps the clip on the recorder for a retry (handled inside it).
  const handleRecordingUse = async (file) => {
    await uploadAndStage(file);
    setSource(SOURCE.UPLOAD);
  };

  const handleSubmit = () => {
    const error = validateComposer(draft);
    if (error) {
      toast.error(error);
      return;
    }

    if (isEditing) {
      const { payload, changed } = buildUpdateCapturePayload(editItem, draft);
      if (!changed) {
        // Nothing edited -- close quietly rather than firing a pointless
        // PATCH or a misleading "updated" toast.
        onClose();
        return;
      }
      updateItem.mutate(
        { id: editItem.id, payload },
        {
          onSuccess: () => {
            toast.success("Capture updated.");
            onClose();
          },
          onError: (err) => {
            console.error("Update capture failed:", err);
            toast.error(
              err?.response?.data?.message || "Could not save. Please try again."
            );
          },
        }
      );
      return;
    }

    const { payload } = buildCreateCapturePayload(draft);

    createItem.mutate(payload, {
      onSuccess: () => {
        /* QA-102 (Osama, 2026-08-04): "Remove the repeated 'Photo saved' (etc.)
           dropdown/toast messages after every capture, across all capture tabs."

           No success toast fires here on purpose. The feedback is already in the
           button — `isLoading={submitting}` puts a spinner in the Capture button
           for the whole round-trip and the modal closes on completion, with the
           new row appearing at the top of the inbox. A toast on top of all three
           was the fourth notification for one action. ERROR toasts stay: a
           failure has no other channel. */
        setDraft(emptyDraft(initialType));
        onClose();
      },
      onError: (err) => {
        console.error("Create capture failed:", err);
        // QA-82/QA-90: if the backend still rejects a note on a video/audio
        // capture (note-widening PR not deployed), say exactly that and that the
        // file is safe — never a dead-end "Could not save".
        toast.error(describeCaptureSaveError(err));
      },
    });
  };

  const isAvTab =
    draft.type === CAPTURE_TYPES.VIDEO || draft.type === CAPTURE_TYPES.AUDIO;
  const recorderMode = recorderModeFor(draft.type);
  // Record is ACTIVE in the Capture composer (full mobile parity): no global
  // flag — the whole Capture surface is already gated by CAPTURE_ENABLED. We
  // only need the browser to actually be able to record + upload an mp4.
  const recordAvailable = isAvTab && canRecordMedia(recorderMode);
  const cameraAvailable =
    draft.type === CAPTURE_TYPES.PHOTO &&
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  // Source options for the current media tab (Upload always present).
  const sourceOptions = [
    { key: SOURCE.UPLOAD, label: "Upload", Icon: CloudUploadOutlinedIcon },
  ];
  if (cameraAvailable) {
    sourceOptions.push({
      key: SOURCE.CAMERA,
      label: "Take photo",
      Icon: PhotoCameraOutlinedIcon,
    });
  }
  if (recordAvailable) {
    sourceOptions.push({
      key: SOURCE.RECORD,
      label: "Record",
      Icon: FiberManualRecordOutlinedIcon,
    });
  }
  // A pre-existing media item being edited (not yet replaced) has no local
  // fileName -- "Uploaded" would be inaccurate for something the user didn't
  // just upload this session.
  const mediaStatusLabel = draft.fileName ? "Uploaded" : "Current file";

  const titleField = (
    <div>
      <FieldLabel htmlFor="capture-title-input">Title</FieldLabel>
      <Input
        id="capture-title-input"
        variant="secondary"
        placeholder="Title"
        value={draft.name}
        data-testid="capture-title-input"
        aria-label="Title"
        // ISP-9999: an Arabic/Hebrew title otherwise renders LTR regardless
        // of the script it holds — auto lets the browser pick per-value.
        dir="auto"
        maxLength={TITLE_MAX_LENGTH}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        containerStyle={{ marginBottom: 0 }}
      />
      <CharCount
        atLimit={draft.name.length >= TITLE_MAX_LENGTH}
        data-testid="capture-title-char-count"
      >
        {draft.name.length}/{TITLE_MAX_LENGTH}
      </CharCount>
    </div>
  );

  const notesField = (
    <div>
      <FieldLabel htmlFor="capture-notes-input">Notes</FieldLabel>
      <Input
        id="capture-notes-input"
        type="textarea"
        variant="secondary"
        placeholder="Description"
        aria-label="Notes"
        value={draft.textContent}
        dir="auto"
        maxLength={NOTES_MAX_LENGTH}
        onChange={(e) => setDraft({ ...draft, textContent: e.target.value })}
        containerStyle={{ marginBottom: 0 }}
      />
      <CharCount
        atLimit={draft.textContent.length >= NOTES_MAX_LENGTH}
        data-testid="capture-notes-char-count"
      >
        {draft.textContent.length}/{NOTES_MAX_LENGTH}
      </CharCount>
    </div>
  );

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      // capq-comp-a11y-cookiedialog: MUI already puts role="dialog" (+ a
      // focus trap + Escape-to-close) on this Paper by default, but not
      // aria-modal -- add it explicitly so AT scopes to this dialog even on
      // a page that also has another role="dialog" element (e.g. the cookie
      // banner) mounted elsewhere in the DOM.
      PaperProps={{ "aria-modal": "true" }}
    >
      <DialogTitle>
        <TextView variant="title">{isEditing ? "Edit capture" : "New capture"}</TextView>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 14, color: colors.darkGrey }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <ModalBody>
          {isEditing ? (
            // Type is immutable once captured (CAP-3) -- show what it is
            // instead of switchable tabs, rather than implying it can change.
            <TextView variant="text" color={colors.textGrey}>
              Editing your {captureTypeLabel(draft.type).toLowerCase()} capture
            </TextView>
          ) : (
            <TypeTabs data-testid="composer-tabs-strip">
              {COMPOSER_TABS.map(({ type, label, Icon, lineArt }) => (
                <TypeTab
                  key={type}
                  type="button"
                  active={draft.type === type}
                  aria-pressed={draft.type === type}
                  onClick={() => setType(type)}
                >
                  {Icon ? (
                    <Icon fontSize="small" />
                  ) : (
                    <ComposerLineArtIcon
                      kind={lineArt}
                      active={draft.type === type}
                    />
                  )}{" "}
                  {label}
                </TypeTab>
              ))}
            </TypeTabs>
          )}

          {draft.type === CAPTURE_TYPES.TEXT ? (
            <>
              {titleField}
              {notesField}
            </>
          ) : draft.type === CAPTURE_TYPES.LINK ? (
            /* QA-98 — "users can copy/paste links there". One field, and the
               refusal names the rule that failed rather than answering every
               bad paste with the same "invalid URL". */
            <>
              <Input
                type="url"
                variant="secondary"
                placeholder="Paste a link — a YouTube video, an image, a page…"
                value={draft.sourceUrl}
                data-testid="capture-link-input"
                aria-label="Link to capture"
                onChange={(e) => setDraft({ ...draft, sourceUrl: e.target.value })}
                onPaste={handleLinkPaste}
                containerStyle={{ marginBottom: 0 }}
              />
              {draft.sourceUrl.trim() && composerError && (
                <TextView
                  variant="tag"
                  color={colors.error}
                  role="status"
                  data-testid="capture-link-error"
                >
                  {composerError}
                </TextView>
              )}
              {draft.sourceUrl.trim() && !composerError && (
                <TextView
                  variant="tag"
                  color={colors.textGrey}
                  data-testid="capture-link-kind"
                >
                  {linkKindHint(draft.sourceUrl)}
                </TextView>
              )}
              {titleField}
              {notesField}
            </>
          ) : (
            <>
              {/* ISP-9999 (composer-file-pick-layout-jump): MediaSlot reserves
                  the empty dropzone's footprint so staging a file below does
                  not shrink/re-centre the dialog under Capture/Cancel. */}
              <MediaSlot
                reserveToggle={sourceOptions.length > 1}
                data-testid="capture-media-slot"
              >
                {/* Source toggle (Upload / Take photo / Record) — only when
                    there is more than one way to add this media type, and
                    nothing is staged yet. */}
                {sourceOptions.length > 1 && !draft.mediaKey && (
                  <TypeTabs role="tablist" aria-label="How to add media">
                    {sourceOptions.map(({ key, label, Icon }) => (
                      <TypeTab
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={source === key}
                        active={source === key}
                        onClick={() => setSource(key)}
                      >
                        <Icon fontSize="small" /> {label}
                      </TypeTab>
                    ))}
                  </TypeTabs>
                )}

                {uploading ? (
                  /* QA-96 — the "Uploading…" wait is the shared BRAND Wheel
                     loader, block-centred, for EVERY media type (upload,
                     webcam shot, or recording), replacing the old MUI
                     CircularProgress that only ever appeared inside the
                     Dropzone. */
                  <UploadingOverlay data-testid="capture-uploading">
                    <BrandWheelLoader variant="medium" label="Uploading…" />
                    <TextView variant="largeText" color={colors.PrimaryPurple700}>
                      Uploading…
                    </TextView>
                  </UploadingOverlay>
                ) : draft.mediaKey ? (
                  <PreviewWrap>
                    {/* QA-90: the staged row reuses the shared
                        CapturePreviewThumb (the same component
                        CaptureCard/AttachedCaptureRow render through)
                        instead of a private PHOTO-or-doc-icon fallback, so a
                        recorded video gets a real poster frame here too, not
                        the document glyph every other non-photo type wore. */}
                    <CapturePreviewThumb
                      item={{
                        type: draft.type,
                        media_url: draft.mediaUrl,
                        text_content: draft.textContent,
                        file_name: draft.fileName,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TextView
                        variant="largeText"
                        color={colors.black}
                        style={{ WebkitLineClamp: 1 }}
                      >
                        {draft.fileName || captureTypeLabel(draft.type)}
                      </TextView>
                      <TextView variant="tag" color={colors.greenText}>
                        {mediaStatusLabel}
                      </TextView>
                    </div>
                    <Button
                      secondary
                      width="auto"
                      style={{ width: 90 }}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          mediaKey: "",
                          mediaUrl: "",
                          fileName: "",
                          name: "",
                        })
                      }
                    >
                      Replace
                    </Button>
                  </PreviewWrap>
                ) : source === SOURCE.CAMERA ? (
                  /* QA-86 — ONE confirm: the shutter stages the shot
                     straight away (autoStage), so there is no second "Use
                     photo" button; the modal's bottom-right "Capture" is the
                     only confirm. */
                  <WebcamPhotoCapture
                    autoStage
                    onCapture={handleWebcamPhoto}
                    onCancel={() => setSource(SOURCE.UPLOAD)}
                  />
                ) : source === SOURCE.RECORD ? (
                  /* QA-86/QA-89 — ONE confirm: stopping stages the clip
                     straight away (autoStage), so there is no second "Use
                     this recording" button and no "stop, then tap
                     Use…" disclaimer. */
                  <MediaRecorder
                    mode={recorderMode}
                    enabled
                    autoStage
                    onUse={handleRecordingUse}
                  />
                ) : (
                  <Dropzone>
                    <CloudUploadOutlinedIcon
                      style={{ color: colors.PrimaryPurple600, fontSize: 30 }}
                    />
                    <TextView variant="largeText" color={colors.PrimaryPurple700}>
                      {draft.type === CAPTURE_TYPES.PHOTO
                        ? "Choose a photo"
                        : draft.type === CAPTURE_TYPES.VIDEO
                        ? "Choose a video"
                        : draft.type === CAPTURE_TYPES.AUDIO
                        ? "Choose an audio file"
                        : "Choose a document"}
                    </TextView>
                    <TextView variant="tag" color={colors.textGrey}>
                      Max {maxSizeMbForType(draft.type)}MB
                    </TextView>
                    <input
                      hidden
                      type="file"
                      data-testid="capture-file-input"
                      accept={acceptFor(draft.type)}
                      disabled={uploading}
                      onChange={handleFile}
                    />
                  </Dropzone>
                )}
              </MediaSlot>

              {/* QA-89: no "Stop the recording, then tap Use this recording."
                  (or "Take a photo, then tap Use photo.") disclaimer any more —
                  autoStage stages the clip/shot the moment it exists, so the
                  two-step it described no longer exists. */}

              {isAvTab && !recordAvailable && source !== SOURCE.RECORD && (
                <TextView variant="tag" color={colors.textGrey}>
                  In-browser recording isn&rsquo;t available in this browser —
                  upload a file instead.
                </TextView>
              )}

              {titleField}
              {notesField}
            </>
          )}

          <ModalActions>
            <ActionBtnWrap>
              <Button secondary onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
            </ActionBtnWrap>
            <ActionBtnWrap>
              <Button
                onClick={handleSubmit}
                isLoading={submitting}
                disabled={submitting || uploading || !!composerError}
              >
                {isEditing ? "Save" : "Capture"}
              </Button>
            </ActionBtnWrap>
          </ModalActions>
        </ModalBody>
      </DialogContent>
    </StyledDialog>
  );
};

export default AddCaptureModal;
